import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import TopBar from "../components/TopBar";
import { doc, getDoc, setDoc, collection, query, orderBy, limit, onSnapshot, addDoc } from "firebase/firestore";
import { db } from "../firebase";

// Small neon card wrapper
function NeonCard({ title, children, footer }) {
  return (
    <div className="rounded-2xl border border-[#D4FF00]/30 bg-black/60 p-4 shadow-[0_0_40px_rgba(212,255,0,0.1)]">
      {title && <div className="text-[#D4FF00] font-semibold mb-2">{title}</div>}
      <div>{children}</div>
      {footer && <div className="pt-3 text-xs text-gray-400">{footer}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview"); // overview | bookings | receipts | sessions | loadouts

  // --- Overview data ---
  const [credits, setCredits] = useState(0);
  const [nextBooking, setNextBooking] = useState(null);
  const [lastSession, setLastSession] = useState(null);

  useEffect(() => {
    if (!user) return;

    // user profile
    const userRef = doc(db, "users", user.uid);
    getDoc(userRef).then(snap => {
      if (!snap.exists()) {
        // create a minimal profile
        setDoc(userRef, { credits: 0, createdAt: new Date(), displayName: user.email || "Player" }, { merge: true });
        setCredits(0);
      } else {
        setCredits(snap.data().credits || 0);
      }
    });

    // next booking (upcoming soonest)
    const qUpcoming = query(
      collection(db, "users", user.uid, "bookings"),
      orderBy("start", "asc"),
      limit(5)
    );
    const unsubUpcoming = onSnapshot(qUpcoming, snap => {
      const upcoming = [];
      snap.forEach(d => {
        const data = d.data();
        if (data.status === "upcoming" && data.start?.toDate) {
          upcoming.push({ id: d.id, ...data });
        }
      });
      setNextBooking(upcoming[0] || null);
    });

    // last session (most recent end)
    const qSessions = query(
      collection(db, "users", user.uid, "sessions"),
      orderBy("end", "desc"),
      limit(1)
    );
    const unsubSessions = onSnapshot(qSessions, snap => {
      let latest = null;
      snap.forEach(d => latest = { id: d.id, ...d.data() });
      setLastSession(latest);
    });

    return () => {
      unsubUpcoming();
      unsubSessions();
    };
  }, [user]);

  // --- Panels ---
  return (
    <div>
      <TopBar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <h1 className="text-3xl font-extrabold tracking-wide text-[#D4FF00] mb-4">
          Account Dashboard
        </h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["overview","bookings","receipts","sessions","loadouts"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-lg border ${
                tab === t
                  ? "border-[#D4FF00] text-black bg-[#D4FF00]"
                  : "border-[#D4FF00]/30 text-gray-200 hover:border-[#D4FF00]/60"
              }`}
            >
              {t[0].toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-3 gap-4">
            <NeonCard title="Balance">
              <div className="text-2xl font-bold">
                {credits >= 0 ? `$${(credits/100).toFixed(2)} CAD` : `-$${(-credits/100).toFixed(2)} CAD`}
              </div>
            </NeonCard>

            <NeonCard title="Next Booking" footer="Manage in Bookings tab">
              {nextBooking ? (
                <div className="space-y-1">
                  <div className="font-semibold">{nextBooking.seatId}</div>
                  <div className="text-gray-300 text-sm">
                    {nextBooking.start?.toDate?.().toLocaleString()} — {nextBooking.end?.toDate?.().toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400">Status: {nextBooking.status}</div>
                </div>
              ) : <div className="text-gray-400">No upcoming bookings.</div>}
            </NeonCard>

            <NeonCard title="Last Session" footer="See full history in Sessions tab">
              {lastSession ? (
                <div className="space-y-1">
                  <div className="font-semibold">{lastSession.seatId}</div>
                  <div className="text-gray-300 text-sm">
                    {lastSession.start?.toDate?.().toLocaleString()} — {lastSession.end?.toDate?.().toLocaleString()}
                  </div>
                  {typeof lastSession.charge === "number" && (
                    <div className="text-sm">Charge: ${(lastSession.charge/100).toFixed(2)}</div>
                  )}
                </div>
              ) : <div className="text-gray-400">No sessions yet.</div>}
            </NeonCard>
          </div>
        )}

        {tab === "bookings" && <BookingsPanel uid={user?.uid} />}
        {tab === "receipts" && <ReceiptsPanel uid={user?.uid} />}
        {tab === "sessions" && <SessionsPanel uid={user?.uid} />}
        {tab === "loadouts" && <LoadoutsPanel uid={user?.uid} />}
      </div>
    </div>
  );
}

// --- Panels ---

function BookingsPanel({ uid }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!uid) return;
    const qRef = query(collection(db, "users", uid, "bookings"), orderBy("start", "desc"));
    const unsub = onSnapshot(qRef, snap => {
      const arr = [];
      snap.forEach(d => arr.push({ id: d.id, ...d.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, [uid]);

  return (
    <div className="grid gap-3">
      {items.length === 0 && <NeonCard><div className="text-gray-400">No bookings yet.</div></NeonCard>}
      {items.map(b => (
        <NeonCard key={b.id} title={`Seat ${b.seatId}`}>
          <div className="text-sm text-gray-300">
            {b.start?.toDate?.().toLocaleString()} — {b.end?.toDate?.().toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Status: {b.status}</div>
          {typeof b.amountPaid === "number" && (
            <div className="text-sm mt-1">Paid: ${(b.amountPaid/100).toFixed(2)}</div>
          )}
        </NeonCard>
      ))}
    </div>
  );
}

function ReceiptsPanel({ uid }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!uid) return;
    const qRef = query(collection(db, "users", uid, "payments"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qRef, snap => {
      const arr = [];
      snap.forEach(d => arr.push({ id: d.id, ...d.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, [uid]);

  return (
    <div className="grid gap-3">
      {items.length === 0 && <NeonCard><div className="text-gray-400">No receipts yet.</div></NeonCard>}
      {items.map(p => (
        <NeonCard key={p.id} title={`$${(p.amount/100).toFixed(2)} ${p.currency || "CAD"}`}>
          <div className="text-sm text-gray-300">{p.method?.toUpperCase()} • {p.status}</div>
          <div className="text-xs text-gray-400 mt-1">
            {p.createdAt?.toDate?.().toLocaleString()}
          </div>
        </NeonCard>
      ))}
    </div>
  );
}

function SessionsPanel({ uid }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!uid) return;
    const qRef = query(collection(db, "users", uid, "sessions"), orderBy("end", "desc"));
    const unsub = onSnapshot(qRef, snap => {
      const arr = [];
      snap.forEach(d => arr.push({ id: d.id, ...d.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, [uid]);

  const total = useMemo(() => items.reduce((s, it) => s + (it.charge || 0), 0), [items]);

  return (
    <div className="grid gap-3">
      <NeonCard title="Summary">
        <div className="text-sm text-gray-300">Sessions: {items.length}</div>
        <div className="text-sm text-gray-300">Total Spent: ${(total/100).toFixed(2)}</div>
      </NeonCard>
      {items.length === 0 && <NeonCard><div className="text-gray-400">No sessions yet.</div></NeonCard>}
      {items.map(s => (
        <NeonCard key={s.id} title={`Seat ${s.seatId}`}>
          <div className="text-sm text-gray-300">
            {s.start?.toDate?.().toLocaleString()} — {s.end?.toDate?.().toLocaleString()}
          </div>
          {typeof s.charge === "number" && (
            <div className="text-sm mt-1">Charge: ${(s.charge/100).toFixed(2)}</div>
          )}
        </NeonCard>
      ))}
    </div>
  );
}

function LoadoutsPanel({ uid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", game: "", dpi: "", sens: "", keybinds: "", graphics: "" });

  useEffect(() => {
    if (!uid) return;
    const qRef = query(collection(db, "users", uid, "loadouts"), orderBy("name", "asc"));
    const unsub = onSnapshot(qRef, snap => {
      const arr = [];
      snap.forEach(d => arr.push({ id: d.id, ...d.data() }));
      setItems(arr);
    });
    return () => unsub();
  }, [uid]);

  const create = async () => {
    if (!uid || !form.name) return;
    await addDoc(collection(db, "users", uid, "loadouts"), {
      name: form.name,
      game: form.game,
      settings: {
        dpi: form.dpi ? Number(form.dpi) : undefined,
        sens: form.sens ? Number(form.sens) : undefined,
        keybinds: form.keybinds || undefined,
        graphics: form.graphics || undefined,
      },
      createdAt: new Date()
    });
    setForm({ name: "", game: "", dpi: "", sens: "", keybinds: "", graphics: "" });
  };

  return (
    <div className="grid gap-4">
      <NeonCard title="New Loadout">
        <div className="grid md:grid-cols-3 gap-3">
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="Name"
                 value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="Game"
                 value={form.game} onChange={e=>setForm({...form, game:e.target.value})}/>
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="DPI"
                 value={form.dpi} onChange={e=>setForm({...form, dpi:e.target.value})}/>
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="Sens"
                 value={form.sens} onChange={e=>setForm({...form, sens:e.target.value})}/>
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="Keybinds"
                 value={form.keybinds} onChange={e=>setForm({...form, keybinds:e.target.value})}/>
          <input className="rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5" placeholder="Graphics"
                 value={form.graphics} onChange={e=>setForm({...form, graphics:e.target.value})}/>
        </div>
        <div className="pt-3">
          <button onClick={create} className="px-4 py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold hover:brightness-90 transition">
            Save Loadout
          </button>
        </div>
      </NeonCard>

      {items.length === 0 && <NeonCard><div className="text-gray-400">No loadouts yet. Create one above.</div></NeonCard>}
      <div className="grid md:grid-cols-2 gap-3">
        {items.map(ld => (
          <NeonCard key={ld.id} title={`${ld.name} ${ld.game ? `• ${ld.game}` : ""}`}>
            <pre className="text-xs text-gray-300 whitespace-pre-wrap">
{JSON.stringify(ld.settings || {}, null, 2)}
            </pre>
          </NeonCard>
        ))}
      </div>
    </div>
  );
}
